/**
 * Pure network-preference resolution — no DOM/import.meta/logger deps, so it is
 * unit-testable in isolation. Consumed by config/index.ts's loadNetworkPreference.
 */

export type NetworkName = 'testnet' | 'mainnet'

/** Minimal storage abstraction so resolution can run against a mock in tests. */
export interface NetworkPrefStore {
  get(key: string): string | null
  set(key: string, value: string): void
  remove(key: string): void
}

/** Versioned flag for the one-time stale-preference reset. */
export const NETWORK_RESET_FLAG = 'x1-network-reset-v1'
export const NETWORK_PREF_KEY = 'x1-explorer-network'

/**
 * Resolve the initial network on load. Encapsulates:
 *  1. ONE-TIME reset migration: the visible network selector was removed and the
 *     explorer defaults everyone to mainnet. Returning users may carry a stale
 *     `x1-explorer-network`='testnet' from when the selector existed — clear it
 *     ONCE (gated on NETWORK_RESET_FLAG) so they flip to the default now.
 *  2. Honor a still-valid deliberate preference afterwards — the hidden "t"
 *     toggle writes `x1-explorer-network` and it must persist across reloads.
 *  3. Otherwise fall back to the configured default (mainnet) and persist it.
 */
export function resolveNetworkOnLoad(
  store: NetworkPrefStore,
  defaultNetwork: NetworkName,
  isAvailable: (n: NetworkName) => boolean,
): NetworkName {
  // 1. one-time reset
  if (!store.get(NETWORK_RESET_FLAG)) {
    store.remove(NETWORK_PREF_KEY)
    store.set(NETWORK_RESET_FLAG, '1')
  }

  // 2. honor a still-valid deliberate preference
  const saved = store.get(NETWORK_PREF_KEY)
  if ((saved === 'testnet' || saved === 'mainnet') && isAvailable(saved)) {
    return saved
  }

  // 3. default (persist for next load)
  if (isAvailable(defaultNetwork)) {
    store.set(NETWORK_PREF_KEY, defaultNetwork)
  }
  return defaultNetwork
}
